// @ts-strict-ignore
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild, output} from "@angular/core";
import {FormArray, FormControl, Validators} from "@angular/forms";
import {MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {MatChipInputEvent} from "@angular/material/chips";
import {Translations} from "@translations";
import {debounceTime} from "rxjs";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

export interface VMAutoCompleteGroup<T> {
	groupDisplayName: string;
	groupValues: VMAutoCompleteNormal<T>[];
}

export interface VMAutoCompleteNormal<T> {
	value: T;
	displayName: string;
}

export interface VMChipListItem<T> extends VMAutoCompleteNormal<T> {}

interface DetailButtonConfig {
	tooltip: string;
}

interface NewItemOptionConfig {
	displayName: string;
}

interface DebounceConfig {
	time: 200;
}

@UntilDestroy()
@Component({
	selector: "sim-base-chip-input",
	templateUrl: "./sim-base-chip-input.component.html",
	styleUrls: ["./sim-base-chip-input.component.scss"],
	standalone: false,
})
export class SimBaseChipInputComponent<T> implements OnInit, OnChanges {
	T = Translations;

	@Input() formState: FormArray<FormControl<T>>;
	@Input() placeholder = "Placeholder";
	@Input() label = "Label";
	@Input() autoCompleteValues: VMAutoCompleteNormal<T>[] = [];
	@Input() hasGroupedValues = false;
	@Input() autoCompleteGroupedValues: VMAutoCompleteGroup<T>[] = [];
	@Input() alternativeColorIds: string[] = [];
	@Input() matAutoCompleteDisplayType: "" | "SimUser" = "";
	@Input() isDisabled = false;
	@Input() isMultiSelect = false;
	@Input() isAutofocusEnabled = true;
	@Input() debounceConfig: DebounceConfig;
	@Input() canAddNewItem: boolean;
	@Input() detailButtonConfig: DetailButtonConfig;
	@Input() displayFn: (v: T) => string = (v) => {
		if (typeof v === "string" || typeof v === "number") {
			return v + "";
		}

		if (typeof v === "object" && v !== null) {
			if ("name" in v) {
				return (v as any).name + "";
			}

			if ("displayName" in v) {
				return (v as any).displayName + "";
			}
		}

		return "Please implement displayFn()";
	};

	itemClicked = output<T>();
	optionSelected = output<T>();
	searchFieldChanged = output<string>();
	createNewItemClicked = output<void>();
	detailButtonClicked = output<void>();
	inputTokenEnd = output<MatChipInputEvent>();
	optionCleared = output<T>();

	@ViewChild("chipInput") inputRef: ElementRef;
	@ViewChild("autoCompleteTrigger")
	autoCompleteTrigger: MatAutocompleteTrigger;

	searchControl = new FormControl("");
	chipList: VMChipListItem<T>[];
	separatorKeysCodes: number[] = [ENTER, COMMA];
	isRequired = false;

	filteredAutoCompleteValues: VMAutoCompleteNormal<T>[] = [];
	filteredAutoCompleteGroupedValues: VMAutoCompleteGroup<T>[] = [];
	public errorCtrl = new FormControl<unknown>(null);

	constructor() {}

	ngOnInit(): void {
		this.isRequired = this.formState.hasValidator(Validators.required);

		this.searchFieldChanged.emit("");

		this.chipList = this.formState.value.map(this.transformToChip);

		this.formState.valueChanges.subscribe((v) => {
			// reload autocomplete if the form got cleared
			if (v.length === 0) {
				this.searchFieldChanged.emit(this.searchControl.value);
			}

			this.chipList = v.map(this.transformToChip);
			this.manageErrorState();
		});

		this.searchControl.valueChanges
			.pipe(debounceTime(this.debounceConfig?.time ?? 0))
			.subscribe((str) => this.searchFieldChanged.emit(str));
	}

	private manageErrorState() {
		this.errorCtrl.setValidators(this.formState.validator);
		this.searchControl.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
			this.errorCtrl.setValue(this.chipList);
			this.errorCtrl.setErrors(this.formState.errors);
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.autoCompleteValues) {
			this.filteredAutoCompleteValues = this.autoCompleteValues.filter(
				(autoVal) => !this.formState.value.some((stateVal) => (stateVal as any)?.id === (autoVal.value as any)?.id),
			);
		}

		if (changes.autoCompleteGroupedValues) {
			this.filteredAutoCompleteGroupedValues = [];

			for (const group of this.autoCompleteGroupedValues) {
				const filteredGroupValues = group.groupValues.filter(
					(autoVal) => !this.formState.value.some((stateVal) => (stateVal as any)?.id === (autoVal.value as any)?.id),
				);

				if (filteredGroupValues.length) {
					this.filteredAutoCompleteGroupedValues.push({
						groupValues: filteredGroupValues,
						groupDisplayName: group.groupDisplayName,
					});
				}
			}
		}

		this.chipList = this.formState.value.map(this.transformToChip);
	}

	autoCompleteOptionSelected(item: VMChipListItem<T>) {
		if (this.isMultiSelect || this.formState.value.length === 0) {
			this.formState.push(new FormControl(item.value));
		} else {
			this.formState.removeAt(0);
			this.formState.push(new FormControl(item.value));
			this.formState.markAsTouched();
		}
		this.searchControl.setValue("");
		this.searchControl.markAsTouched();
		this.optionSelected.emit(item.value);
		this.inputRef.nativeElement.value = "";
		this.searchFieldChanged.emit("");
	}

	onItemClicked(item: T, $event: MouseEvent) {
		this.itemClicked.emit(item);
	}

	onItemRemoved(i: number) {
		if (this.isDisabled) return;

		let removedObject = this.formState.at(i);
		if (!!removedObject) {
			this.formState.removeAt(i);

			this.searchControl.setValue("");
			this.searchControl.markAsTouched();
			this.optionCleared.emit(removedObject?.value);
		}
	}

	private transformToChip: (v: T) => VMChipListItem<T> = (v) => {
		return {
			displayName: this.displayFn(v),
			value: {...v},
		};
	};

	searchClicked() {
		if (this.isMultiSelect) {
			this.inputRef.nativeElement.focus();
		} else {
			this.onItemRemoved(0);
			this.inputRef.nativeElement.focus();
		}
	}

	emitAddNewItem(): void {
		this.createNewItemClicked.emit();
		this.searchControl.reset("");
		this.inputRef.nativeElement.value = "";
		this.searchFieldChanged.emit("");
	}
}
